import axios from "axios";

/**
 * Normalised RBAC API error. `code` is the backend's machine key
 * (e.g. `rbac.coi_blocked`, `rbac.guest_upgrade_required`) and `message` is
 * the server-authoritative, lang-file-driven human copy — surfaced verbatim so
 * the UI never reinvents the verdict text.
 */
export interface RbacApiError {
	status: number | null;
	code: string | null;
	message: string | null;
}

/** Extract the server verdict from an axios error (or any thrown value). */
export const parseRbacApiError = (err: unknown): RbacApiError => {
	if (axios.isAxiosError(err)) {
		const data = err.response?.data as
			| { message?: string; code?: string; error?: string }
			| undefined;
		// Rbac exceptions render their machine code under one of two flat keys
		// depending on how they're dispatched: `code` (ConflictOfInterestException,
		// caught centrally in Handler::render) or `error` (self-rendering
		// exceptions like GuestActionBlockedException/PrivilegeEscalationException).
		// Both are always a flat string, never nested.
		return {
			status: err.response?.status ?? null,
			code: data?.code ?? data?.error ?? null,
			message: data?.message ?? null,
		};
	}
	return { status: null, code: null, message: null };
};

/**
 * A blocked-invite conflict of interest — keyed strictly on the machine code
 * `rbac.coi_blocked`, NOT on any 409: a duplicate-invite/state-conflict 409
 * from these endpoints must fall through to the server's own message, not the
 * COI fallback copy (review F3).
 */
export const isCoiBlocked = (err: RbacApiError): boolean =>
	err.code === "rbac.coi_blocked";

/** Guest tried a mutating action (`rbac.guest_upgrade_required`). */
export const isGuestUpgradeRequired = (err: RbacApiError): boolean =>
	err.code === "rbac.guest_upgrade_required";
